import React, { useState, useRef, useEffect } from "react"
import { useEditor, useValue } from "../../editor-context"
import {
  Menu,
  MenuPopover,
  MenuItems,
  MenuButton,
  MenuItem
} from "@reach/menu-button"
import classNames from "classnames"
import useEventListener from "../../hooks/use-event-listener"
import useSlateDOMNode from "../../hooks/use-slate-dom-node"

import "@reach/menu-button/styles.css"
import "./toolbar.css"
import { getRootBlock } from "../../utils/helpers"

const unknown = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: 0,
  width: 0
}

function getPosition(target, reference) {
  const offsetParent = getOffsetParent(target)
  const offsetRect = getBoundingClientRect(offsetParent)
  const referenceRect = getBoundingClientRect(reference)

  let left = referenceRect.left - offsetRect.left
  let top = referenceRect.top - offsetRect.top + offsetParent.scrollTop

  return { top, left, height: referenceRect.height }
}

function getBoundingClientRect(element) {
  if (!element) return unknown
  return element.getBoundingClientRect()
}

function getOffsetParent(element) {
  return (element && element.offsetParent) || document.documentElement
}

const BlockToolbarMenu = ({
  isOpen,
  setMenuOpen,
  children,
  rootBlock,
  options,
  handleSelect,
  editor
}) => {
  useEffect(() => {
    setMenuOpen(isOpen)
  }, [setMenuOpen, isOpen])

  return (
    <React.Fragment>
      <MenuButton className="block-toolbar-button">
        {children(rootBlock)}
      </MenuButton>
      <MenuPopover
        portal={false}
        className="bg-white shadow rounded-sm p-1 border border-gray-300 absolute"
        style={{ top: "32px" }}
      >
        <MenuItems className="block-toolbar-list">
          {options(rootBlock).map((option, index) =>
            typeof option == "string" ? (
              <div
                style={{
                  textTransform: "uppercase",
                  fontSize: 10,
                  padding: "6px",
                  fontWeight: "bold"
                }}
                key={index}
              >
                {option.label || option}
              </div>
            ) : (
              <MenuItem
                key={index}
                onSelect={() => {
                  handleSelect(editor, rootBlock, option)
                  option.onSelect(editor, rootBlock)
                }}
              >
                {option.label}
              </MenuItem>
            )
          )}
        </MenuItems>
      </MenuPopover>
    </React.Fragment>
  )
}

function Toolbar({
  children = () => null,
  options = () => [],
  onSelect = () => {}
}) {
  const editor = useEditor()
  const value = useValue()
  const target = useRef(null)

  const [active, setActive] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)

  useEventListener(
    document,
    "keydown",
    () => {
      if (active) setActive(false)
    },
    [active]
  )

  useEventListener(
    document,
    "mousemove",
    () => {
      if (!active) setActive(true)
    },
    [active]
  )

  const handleSelect = (editor, rootBlock, option) => {
    onSelect(editor, rootBlock, option)
    setMenuOpen(false)
  }

  const rootBlock = getRootBlock(value, value.startBlock)
  const rootNode = useSlateDOMNode(rootBlock)
  const position = getPosition(target.current, rootNode)

  const className = classNames("block-toolbar", {
    active: active,
    visible: value.selection.isFocused || menuOpen
  })

  return (
    <div
      className={className}
      ref={target}
      style={{
        left: position.left - 42,
        top: position.top,
        height: position.height
      }}
    >
      <Menu className="block-toolbar-menu">
        {({ isOpen }) => (
          <BlockToolbarMenu
            isOpen={isOpen}
            setMenuOpen={setMenuOpen}
            rootBlock={rootBlock}
            options={options}
            handleSelect={handleSelect}
            editor={editor}
          >
            {children}
          </BlockToolbarMenu>
        )}
      </Menu>
    </div>
  )
}

export default Toolbar
